import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

// Replaces the old CategoryPicker + separate flat Price field - the same
// dish is often priced differently by meal-time (lunch cheaper than
// dinner/late-night), so price is now per selected category instead of one
// price for the whole item. Checking a category reveals its own price
// input; unchecking drops that category's price entirely.
//
// Categories are fetched (with ?all=true, i.e. including hidden ones) so
// an item that already has a price under a category the admin later hid
// (e.g. "Mango" during the off-season) still shows it here and stays
// editable, instead of the option just disappearing.
const CategoryPricingPicker = ({ value = [], onChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const entries = Array.isArray(value) ? value : [];

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/category?all=true`)
      .then((res) => setCategories(res.data.categories))
      .catch((err) => console.error("Error fetching categories:", err))
      .finally(() => setLoading(false));
  }, []);

  const isSelected = (slug) => entries.some((e) => e.category === slug);
  const priceFor = (slug) => entries.find((e) => e.category === slug)?.price ?? "";

  const toggle = (slug) => {
    if (isSelected(slug)) {
      onChange(entries.filter((e) => e.category !== slug));
    } else {
      onChange([...entries, { category: slug, price: "" }]);
    }
  };

  const setPrice = (slug, price) => {
    onChange(entries.map((e) => (e.category === slug ? { ...e, price } : e)));
  };

  return (
    <div>
      <label className="block font-semibold mb-1 text-ink">Category &amp; Price</label>
      {loading ? (
        <p className="text-sm text-sub">Loading categories…</p>
      ) : categories.length === 0 ? (
        <p className="text-sm text-sub">
          No categories yet - add one under Categories in the sidebar first.
        </p>
      ) : (
        <div className="space-y-1.5 p-2 border border-line rounded">
          {categories.map((category) => (
            <div key={category.slug} className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-sm text-ink w-40 shrink-0">
                <input
                  type="checkbox"
                  checked={isSelected(category.slug)}
                  onChange={() => toggle(category.slug)}
                />
                {category.name}
                {!category.visible && <span className="text-muted">(hidden)</span>}
              </label>
              {isSelected(category.slug) && (
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Price (৳)"
                  value={priceFor(category.slug)}
                  onChange={(e) => setPrice(category.slug, e.target.value)}
                  className="flex-1 p-1.5 text-sm border border-line rounded"
                />
              )}
            </div>
          ))}
        </div>
      )}
      {entries.length === 0 && (
        <p className="text-xs text-red-600 mt-1">Pick at least one category and set its price.</p>
      )}
    </div>
  );
};

export default CategoryPricingPicker;
